import { DEDICATED_AUTH0_FLOW, FEATURE_FLAGS } from "@white-label-configuration/constants";
import { Route } from "vue-router";
import { jwtDecode } from 'jwt-decode';
import { datadogRum } from "@datadog/browser-rum";
import { getFeatureFlag } from '@white-label-plugin/launch-darkly';
import { getRegionVariable } from '@white-label-helper/get-region-variable';
import * as LZString from 'lz-string';
import { NuxtAppOptions } from '@nuxt/types';
import { AuthObject } from '@white-label-types/auth';
import { useNuxtApp } from '@nuxt/bridge/dist/runtime';

export interface DecodedToken {
  permissions?: string[];
}

/**
 * Checks if the dedicated booking flow feature flag is enabled.
 * @returns True if the feature flag is enabled, false otherwise
 */
export function isDedicatedFeatureFlagOn(): boolean {
  return getFeatureFlag(FEATURE_FLAGS.CUS_2533_DEDICATED_BOOKING_FLOW);
}

/**
 * Checks the current route's auth0flow matches with the given routeName for the dedicated wl flow.
 * @param route Vue Router
 * @param routeName (Optional) route name to check auth0 flow against, defaults to DEDICATED_AUTH0_FLOW
 * @returns True or false if current route matches against name
 */
export const isDedicatedFlow = (route : Route, routeName: string = DEDICATED_AUTH0_FLOW): boolean => {
  return (
    isDedicatedFeatureFlagOn() &&
    (Array.isArray(route?.meta)
      ? route.meta[0]
      : route?.meta)?.auth0Flow === routeName
  );
};

export const getUrlParam = (route: Route, param: string): string => {
  return route?.query?.[param]?.toString();
}

/**
 * Decodes a JWT token
 * @param token The JWT token to decode
 * @returns An object with the decoded token or an empty object if decoding fails
 */
export const decodeToken = (token: string): DecodedToken => {
  try {
    return jwtDecode<DecodedToken>(token.replace('Bearer ', ''));
  } catch (error) {
    datadogRum.addError(
      new Error('Auth decodeToken:', { cause: error }),
      {
        file: 'white-label-helper > dedicated-flow > decodeToken()',
        token: token || ''
      }
    );
    return {};
  }
}

/**
 * Returns the permissions property from the decoded token
 * @param token The JWT token to decode
 * @returns An array of permissions or an empty array if decoding fails
 */
export const getUserPermissions = (token: string): string[] => {
  const decodedToken = decodeToken(token);
  return decodedToken?.['permissions'] || [];
}

/**
 * Redirects the customer service agent back to the extranet
 * @param orderReference - Order reference id
 */
export function redirectToExtranet(orderReference: string) {
  const nuxtApp: NuxtAppOptions = useNuxtApp();

  const redirectUrl = nuxtApp['$cookies'].get(`auth0.${nuxtApp['$config']['AUTH0_PARTNER_CLIENT_ID']}.returnUrl`);
  const partnerRegionCode = getRegionVariable('code')

  if (isDedicatedFlow(nuxtApp['$route'])) {
    window.location.assign(`${redirectUrl}?showOrdersList=true&orderId=${orderReference}&partnerRegion=${partnerRegionCode}`);
  }
}

/**
 * Tries to decode the sso token from cookies if exists,
 * otherwise refreshes user state from auth obj.
 * @param auth
 * @param ssoToken
 */
export async function handleSSOToken(auth: AuthObject, ssoToken: string): Promise<void> {
  if (ssoToken) {
    let decodedSSO;
    if (getFeatureFlag(FEATURE_FLAGS.FEAT_CUS_3290_ENABLE_COOKIE_ENCODING)) {
      decodedSSO = LZString.decompressFromEncodedURIComponent(ssoToken);
    } else {
      decodedSSO = Buffer.from(ssoToken, 'base64').toString('utf-8');
    }
    const { token, tokenExpiration } = JSON.parse(decodedSSO);
    await auth.fetchUser(token);
    auth.setUserToken(token, tokenExpiration);
    // clean up the sso_token cookie
    const nuxtApp: NuxtAppOptions = useNuxtApp();
    nuxtApp['$cookies'].remove('sso_token');
  } else {
    await auth.refreshUser();
    await auth.checkSSOSession();
  }
}